import React, { useContext, useReducer } from 'react';
import proptypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import { MiniSwatch } from './mini-swatch';
import { getSwatchWrapperClasses } from './mini-swatch-utils';
import { useSwatchState } from './hooks/useSwatchState';
import { useSwatchDisplay } from './hooks/useSwatchDisplay';
import { useSwatchAnalytics } from './hooks/useSwatchAnalytics';
import { MAX_NUMBER_OF_MINI_SWATCHES } from '../../statics';
import SskuMiniSwatchesReducer from './ssku-mini-swatches-reducer';

const {
  string, func, number, bool, shape, oneOf
} = proptypes;

// Initial state for the reducer
const initialState = {
  swatches: [],
  parentId: null,
  shouldSkip: false,
  isSwatchClicked: false,
  selectedItemLabel: null,
  totalNumberOfOptions: 0,
  itemId: null,
  isHovered: false
};

export const SuperSkuMiniSwatches = ({
  itemId,
  onChange,
  onHover,
  numberOfMiniSwatches,
  center,
  componentImpressionData,
  isImpressionsRequired,
  showLabelWithSwatches,
  removeStaticHeight,
  hideComponentWhenEmpty,
  hideSwatchBorder,
  swatchMediaSize,
  isolateTapEvents
}) => {
  const { channel = 'desktop' } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { greater } = useBreakpoint('sm');
  const enableHover = Boolean(greater);

  const swatchNavigatestoPip = useConfigService('fs-prop:isSwatchNavigatestoPIPEnabled') || false;

  const [state, dispatch] = useReducer(SskuMiniSwatchesReducer, initialState);

  const { data } = useDataModel('product', {
    variables: { itemId },
    fetchPolicy: 'cache-first'
  });

  const { additionalData = {} } = useImpression({
    data: {
      id: itemId,
      component: 'SuperSkuMiniSwatches',
      name: 'SuperSkuMiniSwatches',
      position: 0
    }
  });

  // impressions data
  const { parent } = additionalData;

  // product data
  const { parentId, canonicalUrl } = data?.product?.identifiers || {};
  const {
    swatches: swatchesData = [],
    totalNumberOfOptions: totalNumberOfOptionsAvailable = 0
  } = data?.product?.info || {};

  // Custom hooks for swatch functionality
  const { handleAttributeValueClick, handleAttributeValueHover, handleResetHover } = useSwatchState({
    state,
    dispatch,
    data,
    parentId,
    onChange,
    swatchNavigatestoPip,
    onHover,
    itemId
  });

  const { handleOptionsLinkClick } = useSwatchAnalytics({
    itemId,
    parent
  });

  // Always 5 maximum unless prop is passed. Using container queries to control hiding as needed.
  const numberOfSwatchesToDisplay = numberOfMiniSwatches || MAX_NUMBER_OF_MINI_SWATCHES;

  const {
    swatchesToDisplay,
    displaySwatchesSpace,
    displaySwatches,
    showMoreOptionsLink,
    moreOptionsText,
    hoveredSwatchLabelClasses,
    sskuSwatchContainerClasses,
    optionsLinkClasses
  } = useSwatchDisplay({
    state,
    swatchesData,
    totalNumberOfOptionsAvailable,
    numberOfSwatchesToDisplay,
    isMobile,
    center,
    removeStaticHeight
  });

  if (hideComponentWhenEmpty && (!displaySwatchesSpace && !showMoreOptionsLink)) {
    return <></>;
  }

  const sskuContainerClasses = classNames({
    'sui-relative sui-z-20 sui-w-fit [&_button]:sui-pointer-events-auto': isolateTapEvents
  });

  return (
    <div className="sui-@container/ssms sui-w-full">
      <div
        data-testid="ssku-min-swatch"
        data-component="SuperSkuMiniSwatches"
        className={sskuContainerClasses}
      >
        {displaySwatches && showLabelWithSwatches && (
          <div className={hoveredSwatchLabelClasses}>
            {state?.selectedItemLabel && (
              <div>{state.selectedItemLabel}</div>
            )}
          </div>
        )}
        {/* Container queries require @container/ssms context from parent component */}
        <div className={sskuSwatchContainerClasses}>
          {displaySwatches && swatchesToDisplay.map((swatch, index) => {
            const {
              isSelected = false,
              itemId: swatchItemId,
              label = '',
              swatchImgUrl
            } = swatch || {};

            if (!swatchImgUrl) return null;

            return (
              <MiniSwatch
                swatchGuid={swatchImgUrl}
                value={label || ''}
                isSelected={isSelected}
                enableHover={enableHover}
                onClick={() => handleAttributeValueClick(swatchItemId)}
                onMouseEnter={(() => handleAttributeValueHover(swatchItemId))}
                onMouseLeave={handleResetHover}
                swatchItemId={swatchItemId}
                componentImpressionData={componentImpressionData || {}}
                isImpressionsRequired={isImpressionsRequired}
                wrapperClasses={getSwatchWrapperClasses(index)}
                swatchSize={swatchMediaSize}
                withoutTileBorder={hideSwatchBorder}
                key={`ssku-${swatchImgUrl}`}
                ariaLabel={`${label} swatch`}
                swatchName={label}
                index={index}
              />
            );
          })}
          {showMoreOptionsLink && (
            <a
              className="sui-flex sui-flex-col"
              aria-label="product show more options link"
              href={canonicalUrl}
              onClick={handleOptionsLinkClick}
            >
              <div className={optionsLinkClasses}>
                {moreOptionsText}
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

SuperSkuMiniSwatches.propTypes = {
  itemId: string.isRequired,
  onChange: func.isRequired,
  onHover: func,
  numberOfMiniSwatches: number,
  center: bool,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
    anchorSku: string
  }),
  isImpressionsRequired: bool,
  showLabelWithSwatches: bool,
  removeStaticHeight: bool,
  hideComponentWhenEmpty: bool,
  hideSwatchBorder: bool,
  /** Default '7'; See Stencil Design System, Spacing Tokens for available pixel values */
  swatchMediaSize: oneOf(['4', '5', '6', '7', '8', '9']),
  isolateTapEvents: bool
};

SuperSkuMiniSwatches.defaultProps = {
  onHover: () => { },
  numberOfMiniSwatches: null,
  center: false,
  componentImpressionData: {
    name: '',
    component: '',
    position: 1,
    type: '',
    anchorSku: ''
  },
  isImpressionsRequired: false,
  /* Reserves space above mini swatches for swatch label text. */
  showLabelWithSwatches: false,
  /* Remove constant height styles, allow flexible component height */
  removeStaticHeight: false,
  /* Return react fragment element when no swatches or links to show */
  hideComponentWhenEmpty: false,
  /* Hide swatch border, used to hide border for swatches in smaller spaces */
  hideSwatchBorder: false,
  /* Default '7'; See Stencil Design System, Spacing Tokens for available pixel values */
  swatchMediaSize: '7',
  isolateTapEvents: false
};

SuperSkuMiniSwatches.dataModel = {
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    identifiers: shapeType({
      canonicalUrl: stringType(),
      parentId: stringType()
    }),
    info: shapeType({
      swatches: arrayOfType(shapeType({
        isSelected: boolType(),
        itemId: stringType(),
        label: stringType(),
        swatchImgUrl: stringType(),
        url: stringType(),
        value: stringType()
      })),
      totalNumberOfOptions: numberType()
    })
  })
};
